import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import ScoreBars from "@/components/ScoreBars";
import {
  Library, Trash2, ScanText, Loader2, User, Sparkles, Music, FileText, BookOpen, Gem,
  Award, Share2, Wand2, Orbit, BookmarkX,
} from "lucide-react";
import { toast } from "sonner";

const SECTION_ICONS = {
  ScanText, User, Sparkles, Music, FileText, BookOpen, Gem, Award, Share2, Wand2, Orbit, Library,
};

function EmptyState({ label, onDecode }) {
  return (
    <div className="litra-card p-8 text-center" data-testid="vault-empty">
      <Library className="w-7 h-7 mx-auto text-[var(--bone-faint)] mb-2" />
      <p className="text-sm text-[var(--bone-muted)]">Nothing in <span className="text-[var(--bone)]">{label}</span> yet.</p>
      <p className="text-xs text-[var(--bone-faint)] mt-1">Earn items in Decode, Studio, Doctrine, Path &amp; Universe.</p>
      <Button variant="outline" className="glass mt-4" onClick={onDecode} data-testid="vault-empty-decode"><ScanText className="w-4 h-4 mr-2" /> Start decoding</Button>
    </div>
  );
}

export default function Vault() {
  const navigate = useNavigate();
  const [sections, setSections] = useState([]);
  const [counts, setCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [active, setActive] = useState("words");
  const [section, setSection] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadOverview = useCallback(async () => {
    try {
      const r = await api.get("/vault/overview");
      setSections(r.data.sections);
      setCounts(r.data.counts || {});
      setTotal(r.data.total || 0);
    } catch (e) { /* ignore */ }
  }, []);

  const loadSection = useCallback(async (id) => {
    setLoading(true);
    try {
      const r = await api.get(`/vault/section/${id}`);
      setSection(r.data);
    } catch (e) { setSection(null); } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => { loadSection(active); }, [active, loadSection]);

  const select = (id) => setActive(id);
  const refresh = () => { loadOverview(); loadSection(active); };

  const removeWord = async (id) => { try { await api.delete(`/vault/${id}`); toast.success("Removed"); refresh(); } catch (e) { /* ignore */ } };
  const removeShareCard = async (id) => { try { await api.delete(`/vault/share-card/${id}`); toast.success("Removed"); refresh(); } catch (e) { /* ignore */ } };
  const unsaveReport = async (rid) => { try { await api.post("/vault/report", { report_id: rid, saved: false }); toast.success("Removed"); refresh(); } catch (e) { /* ignore */ } };
  const unsaveStudio = async (id) => { try { await api.post(`/create/save/${id}`, { saved: false }); toast.success("Removed"); refresh(); } catch (e) { /* ignore */ } };

  const studioSnippet = (out) => {
    if (!out) return "";
    for (const k of ["taglines", "captions", "bios", "slogans", "hooks", "handles", "domains", "titles", "prompts"]) {
      if (Array.isArray(out[k]) && out[k].length) return out[k][0];
    }
    if (Array.isArray(out.names) && out.names.length) return out.names.map((n) => n.input).slice(0, 4).join(" · ");
    if (out.mission) return out.mission;
    if (out.analysis) return out.analysis;
    if (out.card_text) return out.card_text;
    return "";
  };

  const activeSection = sections.find((s) => s.id === active);

  const renderItems = () => {
    if (loading) return <Loader2 className="w-5 h-5 animate-spin text-[var(--bone-faint)]" data-testid="vault-loading" />;
    if (!section) return null;
    const { kind, items } = section;

    // Universe progress is an object, not a list.
    if (kind === "universe") {
      const u = items || {};
      const nodes = [...new Set([...(u.rewarded_nodes || []), ...(u.path_nodes || [])])];
      const realms = u.mastered_realms || [];
      if (nodes.length === 0 && realms.length === 0) return <EmptyState label="Universe Progress" onDecode={() => navigate("/universe")} />;
      return (
        <div className="grid sm:grid-cols-2 gap-3" data-testid="vault-items">
          <div className="litra-card p-4">
            <div className="flex items-center gap-2 mb-1"><Orbit className="w-4 h-4 green-text" /><span className="font-display font-semibold">Nodes lit</span></div>
            <div className="text-3xl font-display green-text">{nodes.length}</div>
            <div className="text-xs text-[var(--bone-faint)] mt-1">Across all realms</div>
          </div>
          <div className="litra-card p-4">
            <div className="flex items-center gap-2 mb-1"><Award className="w-4 h-4 gold-text" /><span className="font-display font-semibold">Realms mastered</span></div>
            <div className="text-3xl font-display gold-text">{realms.length}</div>
            <div className="text-xs text-[var(--bone-faint)] mt-1">{realms.length ? realms.join(", ") : "Master realms in Universe"}</div>
          </div>
          <div className="sm:col-span-2"><Button variant="outline" className="glass w-full" onClick={() => navigate("/universe")} data-testid="vault-goto-universe"><Orbit className="w-4 h-4 mr-2" /> Open Universe</Button></div>
        </div>
      );
    }

    if (!items || items.length === 0) {
      return <EmptyState label={activeSection?.label || "this section"} onDecode={() => navigate(kind === "studio" ? "/studio" : kind === "lesson" ? "/doctrine" : "/decode")} />;
    }

    return (
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="vault-items">
        {items.map((it, idx) => {
          // Word-based items
          if (kind === "word") return (
            <div key={it.id} className="litra-card p-4" data-testid="vault-item-word">
              <div className="flex items-start justify-between gap-2">
                <div><div className="font-display font-semibold">{it.word}</div>
                  {it.rule_summary && <div className="text-xs text-[var(--bone-faint)] mt-0.5">{it.rule_summary.root} → {it.rule_summary.seal} · {it.rule_summary.archetype}</div>}</div>
                <Button size="icon" variant="ghost" onClick={() => removeWord(it.id)} data-testid={`vault-delete-${it.id}`} aria-label="Remove"><Trash2 className="w-4 h-4 text-[#C24B4B]" /></Button>
              </div>
              {it.rule_summary && <Badge variant="outline" className="mt-2 text-[10px]">{it.rule_summary.word_class}</Badge>}
            </div>
          );

          if (kind === "report") return (
            <div key={it.id} className="litra-card p-4" data-testid="vault-item-report">
              <div className="flex items-start justify-between gap-2">
                <div><div className="font-display font-semibold">{it.input_text}</div>
                  <div className="text-xs text-[var(--bone-faint)] mt-0.5">{it.decode_type} · {it.word_class}</div></div>
                <Button size="icon" variant="ghost" onClick={() => unsaveReport(it.id)} aria-label="Remove"><BookmarkX className="w-4 h-4 text-[#C24B4B]" /></Button>
              </div>
              {it.scores_json && <div className="mt-2"><ScoreBars scores={it.scores_json} compact limit={3} /></div>}
            </div>
          );

          if (kind === "lesson") return (
            <div key={it.id} className="litra-card p-4" data-testid="vault-item-lesson">
              <div className="flex items-center gap-2 mb-1"><BookOpen className="w-4 h-4 green-text" /><span className="font-display font-semibold text-sm">{it.title}</span></div>
              <div className="flex items-center gap-2">
                {it.year != null && <Badge variant="outline" className="text-[10px]">Year {it.year}</Badge>}
                {it.quiz_score != null && <Badge variant="outline" className="text-[10px] green-text border-[rgba(20,90,67,0.5)]">Quiz {it.quiz_score}%</Badge>}
              </div>
            </div>
          );

          if (kind === "artifact") return (
            <div key={it.id || idx} className="litra-card gold-edge p-4" data-testid="vault-item-artifact">
              <div className="flex items-center gap-2 mb-1"><Gem className="w-4 h-4 gold-text" /><span className="font-display font-semibold text-sm gold-text">{it.name}</span></div>
              {it.rarity && <Badge className="gold-edge gold-text bg-transparent text-[10px] capitalize">{it.rarity}</Badge>}
              {it.desc && <div className="text-xs text-[var(--bone-faint)] mt-1.5">{it.desc}</div>}
            </div>
          );

          if (kind === "certificate") return (
            <div key={it.id} className="litra-card gold-edge p-4" data-testid="vault-item-certificate">
              <div className="flex items-center gap-2 mb-1"><Award className="w-4 h-4 gold-text" /><span className="font-display font-semibold text-sm gold-text">{it.title}</span></div>
              <div className="text-xs text-[var(--bone-faint)]">{it.description}</div>
            </div>
          );

          if (kind === "share_card") return (
            <div key={it.id} className="litra-card p-4" data-testid="vault-item-sharecard">
              <div className="flex items-start justify-between gap-2">
                <span className="font-display font-semibold">{it.word}</span>
                <Button size="icon" variant="ghost" onClick={() => removeShareCard(it.id)} aria-label="Remove"><Trash2 className="w-4 h-4 text-[#C24B4B]" /></Button>
              </div>
              {it.bind_statement && <div className="mono-chip gold-text mt-1">{it.bind_statement}</div>}
              {it.card_text && <div className="text-xs text-[var(--bone-muted)] mt-2 leading-relaxed">{it.card_text}</div>}
              {it.signals?.length > 0 && <div className="mt-2 flex flex-wrap gap-1">{it.signals.map((s, i) => <span key={i} className="mono-chip glass rounded-full px-2 py-0.5 text-[10px]">{s}</span>)}</div>}
            </div>
          );

          if (kind === "studio") return (
            <div key={it.id} className="litra-card p-4" data-testid="vault-item-studio">
              <div className="flex items-start justify-between gap-2">
                <div><div className="flex items-center gap-1.5"><Wand2 className="w-3.5 h-3.5 green-text" /><span className="font-display font-semibold text-sm">{it.tool_name || it.tool_type}</span></div>
                  <div className="text-xs text-[var(--bone-faint)] mt-0.5">on "{it.input_text}"</div></div>
                <Button size="icon" variant="ghost" onClick={() => unsaveStudio(it.id)} aria-label="Remove"><BookmarkX className="w-4 h-4 text-[#C24B4B]" /></Button>
              </div>
              {studioSnippet(it.output_json) && <div className="text-xs text-[var(--bone-muted)] mt-2 leading-relaxed line-clamp-3">{studioSnippet(it.output_json)}</div>}
            </div>
          );

          return null;
        })}
      </div>
    );
  };

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-display font-semibold flex items-center gap-2"><Library className="w-6 h-6 green-text" /> Word Vault</h1>
          <p className="text-[var(--bone-muted)] text-sm">{total} saved item{total === 1 ? "" : "s"} across 12 sections — words, reports, lessons, artifacts and more.</p>
        </div>
        <Button variant="outline" className="glass" onClick={() => navigate("/decode")} data-testid="vault-decode-button"><ScanText className="w-4 h-4 mr-2" /> Decode more</Button>
      </div>

      {/* Section selector */}
      <div className="flex flex-wrap gap-1.5" data-testid="vault-section-selector">
        {sections.map((s) => {
          const Icon = SECTION_ICONS[s.icon] || Library;
          const isActive = active === s.id;
          const c = counts[s.id] || 0;
          return (
            <button key={s.id} onClick={() => select(s.id)} data-testid={`vault-section-${s.id}`}
              className={`flex items-center gap-1.5 mono-chip rounded-full pl-2.5 pr-3 py-1.5 border transition-colors ${isActive ? "border-[rgba(20,90,67,0.6)] bg-[rgba(20,90,67,0.12)] green-text" : "border-white/[0.08] text-[var(--bone-faint)] hover:bg-white/[0.03]"}`}>
              <Icon className="w-3.5 h-3.5" /> {s.label} {c ? <span className={isActive ? "text-[var(--bone)]" : ""}>({c})</span> : null}
            </button>
          );
        })}
      </div>

      {renderItems()}
    </div>
  );
}
